import { toast } from 'sonner'
import { api, extractApiError } from './api'

const RZP_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js'

/** Inject the Razorpay Checkout script on demand. Resolves false if it
 *  can't be fetched (offline / blocked). */
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true)
    const existing = document.querySelector(`script[src="${RZP_SCRIPT}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve(true))
      existing.addEventListener('error', () => resolve(false))
      return
    }
    const s = document.createElement('script')
    s.src = RZP_SCRIPT
    s.onload = () => resolve(true)
    s.onerror = () => resolve(false)
    document.body.appendChild(s)
  })
}

/**
 * Run the full Razorpay checkout for a plan: create the order, open the
 * hosted widget, and verify the payment server-side on success. Shared
 * by the Billing page and the billing-lock modal.
 *
 * @param {object}   opts
 * @param {string}   opts.planCode   Plan code to pay for.
 * @param {string}   [opts.cadence]  'monthly' | 'yearly' (default monthly).
 * @param {string}   [opts.couponCode] Optional promo code — validated +
 *                                   applied server-side when building the
 *                                   order. An invalid code surfaces as an
 *                                   error toast and the checkout is aborted.
 * @param {object}   [opts.user]     Current user — used to prefill the widget.
 * @param {Function} [opts.onSuccess] Awaited after verification succeeds.
 * @param {Function} [opts.onClose]  Called when the widget closes (dismiss
 *                                   or after the success/failure handler).
 * @returns {Promise<boolean>} true if the widget opened.
 */
export async function startRazorpayCheckout({
  planCode,
  cadence = 'monthly',
  couponCode,
  user,
  onSuccess,
  onClose,
}) {
  const loaded = await loadRazorpayScript()
  if (!loaded) {
    toast.error('Could not load the Razorpay checkout. Check your connection.')
    onClose?.()
    return false
  }

  let order
  try {
    const body = { planCode, cadence }
    // Only send the field when set, so a blank input doesn't override
    // the tenant's remembered sign-up code (the server falls back to it).
    if (couponCode && couponCode.trim()) body.couponCode = couponCode.trim()
    const res = await api.post('/payments/order', body)
    order = res.data
  } catch (err) {
    toast.error(extractApiError(err).message)
    onClose?.()
    return false
  }

  // Confirm the discount landed so the customer isn't surprised by the
  // widget total — only when the server actually applied a coupon.
  if (order.couponCode && order.discountAmount > 0) {
    toast.success(`Coupon ${order.couponCode} applied.`)
  }

  const rzp = new window.Razorpay({
    key: order.keyId,
    amount: order.amount,
    currency: order.currency,
    order_id: order.orderId,
    name: order.companyName || 'TaskMagix',
    description: `${order.planName} — ${cadence} subscription`,
    prefill: {
      name: user?.fullName ?? '',
      email: user?.emailId ?? '',
    },
    theme: { color: '#1C7249' },
    handler: async (resp) => {
      try {
        await api.post('/payments/verify', {
          razorpayOrderId: resp.razorpay_order_id,
          razorpayPaymentId: resp.razorpay_payment_id,
          razorpaySignature: resp.razorpay_signature,
        })
        toast.success('Payment successful — your subscription is now active.')
        if (onSuccess) await onSuccess()
      } catch (err) {
        toast.error(extractApiError(err).message)
      } finally {
        onClose?.()
      }
    },
    modal: { ondismiss: () => onClose?.() },
  })
  rzp.on('payment.failed', (resp) => {
    toast.error(resp?.error?.description || 'The payment failed.')
  })
  rzp.open()
  return true
}
